import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Eye, EyeOff, Trash2, Film, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, apiCall } from "../calendar/calendarUtils";
import { ExerciseEditor } from "./ExerciseEditor";
import { MembersPanel } from "./MembersPanel";
import { PlanTemplatesPanel } from "./PlanTemplatesPanel";
import { MotivationPanel } from "./MotivationPanel";
import { ShopPanel, LivePanel } from "./ShopLivePanels";
import { ChallengesPanel } from "./CommunityPanels";
import { VideoReviewPanel, Badge } from "./VideoReviewPanel";

const TIER = { free: "Kostenlos", premium: "Premium", premium_plus: "Plus" };

function Stats({ s }) {
  if (!s) return null;
  const tiles = [["Mitglieder aktiv", s.members_active], ["Warten auf Eltern", s.members_pending_parent], ["Premium", s.by_tier.premium], ["Premium Plus", s.by_tier.premium_plus],
    ["Übungen online", s.exercises_published], ["Entwürfe", s.exercises_draft], ["Absolvierte Einheiten", s.completions_total]];
  return <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="training-admin-stats">{tiles.map(([l, v]) => <div key={l} className="bg-[#121212] border border-white/10 p-4"><p className="text-2xl font-bold text-white">{v}</p><p className="text-xs text-[#71717A]">{l}</p></div>)}</div>;
}

function ExerciseList({ token, meta }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(undefined);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => apiCall(token, "/training/admin/exercises").then((d) => setItems(d.items)).catch((e) => toast.error(e.message)), [token]);
  useEffect(() => { load(); }, [load]);
  const shown = useMemo(() => items.filter((x) => !filter || x.status === filter || x.categories.includes(filter)), [items, filter]);
  const setStatus = async (ex, status) => { try { await apiCall(token, `/training/admin/exercises/${ex.exercise_id}/status`, { method: "POST", body: { status } }); load(); } catch (e) { toast.error(e.message); } };
  const del = async (ex) => { if (!window.confirm(`„${ex.title}“ endgültig löschen?`)) return; await apiCall(token, `/training/admin/exercises/${ex.exercise_id}`, { method: "DELETE" }); toast.success("Gelöscht"); load(); };
  const catLabel = (id) => meta.categories.find((c) => c.id === id)?.label || id;
  return (
    <div className="space-y-4" data-testid="training-exercise-list">
      <div className="flex flex-wrap gap-2 items-center">
        <Button onClick={() => setEdit(null)} className="bg-[#c1ff72] text-black hover:bg-[#a8eb54] rounded-none" data-testid="exercise-new"><Plus className="w-4 h-4 mr-1" />Neue Übung</Button>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-10 px-2 bg-[#0A0A0A] border border-white/15 text-white text-sm" data-testid="exercise-admin-filter">
          <option value="">Alle ({items.length})</option><option value="published">Veröffentlicht</option><option value="draft">Entwürfe</option>
          {meta.categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>
      <div className="divide-y divide-white/5 border border-white/10">
        {shown.map((ex) => (
          <div key={ex.exercise_id} className="flex flex-wrap items-center gap-3 p-3" data-testid={`admin-exercise-${ex.slug}`}>
            <div className="flex-1 min-w-[200px]"><p className="text-white flex items-center gap-2">{ex.title}{ex.video?.status === "approved" && <Film className="w-3.5 h-3.5 text-[#c1ff72]" />}{ex.video_candidate?.status === "needs_review" && <Badge s="needs_review" testId={`admin-exercise-video-review-${ex.slug}`} />}</p>
              <p className="text-xs text-[#71717A]">{ex.categories.map(catLabel).join(", ")} · {TIER[ex.access]} · {ex.duration_min} Min</p></div>
            <span className={`text-xs px-2 py-0.5 ${ex.status === "published" ? "bg-green-500/15 text-green-300" : "bg-yellow-400/15 text-yellow-200"}`} data-testid={`admin-exercise-status-${ex.slug}`}>{ex.status === "published" ? "Online" : "Entwurf"}</span>
            {ex.status === "published" && <a href={`/training/uebung/${ex.slug}`} target="_blank" rel="noreferrer" className="text-[#71717A] hover:text-white"><ExternalLink className="w-4 h-4" /></a>}
            <button onClick={() => setStatus(ex, ex.status === "published" ? "draft" : "published")} title={ex.status === "published" ? "Zurückziehen" : "Veröffentlichen"} className="text-[#A1A1AA] hover:text-white" data-testid={`admin-exercise-toggle-${ex.slug}`}>{ex.status === "published" ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
            <button onClick={() => setEdit(ex)} className="text-[#A1A1AA] hover:text-white" data-testid={`admin-exercise-edit-${ex.slug}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => del(ex)} className="text-red-300" data-testid={`admin-exercise-delete-${ex.slug}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <ExerciseEditor token={token} meta={meta} exercise={edit} open={edit !== undefined} onClose={() => setEdit(undefined)}
        onSaved={() => { setEdit(undefined); load(); }} onMedia={(ex) => { if (ex) setEdit(ex); load(); }} />
    </div>
  );
}

export default function TrainingAdminTab({ adminToken }) {
  const [sub, setSub] = useState("uebungen");
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetch(`${API}/training/meta`).then((r) => r.json()).then(setMeta);
    apiCall(adminToken, "/training/admin/stats").then(setStats).catch(() => {});
  }, [adminToken, sub]);
  if (!meta) return null;
  return (
    <div className="space-y-6" data-testid="training-admin-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-semibold text-white">Trainings-App</h2>
        <a href="/training" target="_blank" rel="noreferrer" className="text-sm text-[#c1ff72] flex items-center gap-1">Trainings-App öffnen<ExternalLink className="w-3.5 h-3.5" /></a>
      </div>
      <Stats s={stats} />
      <div className="flex gap-2 flex-wrap">
        {[["uebungen", "Übungen"], ["videos", "Videos prüfen & verwalten"], ["plaene", "Wochenpläne"], ["motivation", "Motivation"], ["mitglieder", "Mitglieder"], ["shop", "Preise, Gutscheine & Zahlungen"], ["live", "Live-Trainings"], ["challenges", "Challenges"]].map(([id, l]) => (
          <button key={id} onClick={() => setSub(id)} className={`px-4 py-2 text-sm border ${sub === id ? "bg-white text-black border-white" : "border-white/20 text-white"}`} data-testid={`training-sub-${id}`}>{l}</button>
        ))}
      </div>
      {sub === "uebungen" && <ExerciseList token={adminToken} meta={meta} />}
      {sub === "videos" && <VideoReviewPanel token={adminToken} />}
      {sub === "shop" && <ShopPanel token={adminToken} />}
      {sub === "live" && <LivePanel token={adminToken} />}
      {sub === "challenges" && <ChallengesPanel token={adminToken} />}
      {sub === "plaene" && <PlanTemplatesPanel token={adminToken} meta={meta} />}
      {sub === "motivation" && <MotivationPanel token={adminToken} />}
      {sub === "mitglieder" && <MembersPanel token={adminToken} />}
    </div>
  );
}
